import { useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Megaphone, Send, Trash2, Users, Building2 } from 'lucide-react'
import { Breadcrumb } from '../../components/Breadcrumb'
import { DataTable } from '../../components/table/DataTable'
import { RowActionsMenu } from '../../components/table/RowActionsMenu'
import { api, extractApiError } from '../../lib/api'
import { announcementListResponse } from '../../lib/types'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { Button } from '../../components/ui/Button'
import { TextField, FieldLabel } from '../../components/ui/Field'
import { formatDateTime, formatNumber } from '../../lib/format'

const TITLE_MAX = 100
const BODY_MAX = 1000

function fmtDate(iso) {
  if (!iso) return '—'
  return formatDateTime(iso)
}

const AUDIENCE_LABEL = {
  all_users: 'All members',
  company_admins: 'Company Admins',
}

export function AnnouncementsPage() {
  const qc = useQueryClient()
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [bulkDel, setBulkDel] = useState(null) // { ids, clearSelection } | null
  const [bulkRunning, setBulkRunning] = useState(false)

  // Compose form — plain controlled state (only four fields, no need to
  // pull in react-hook-form here).
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [audience, setAudience] = useState('all_users')
  const [priority, setPriority] = useState('normal')
  const [touched, setTouched] = useState(false)

  const listQuery = useQuery({
    queryKey: ['platform-announcements'],
    queryFn: async () => {
      const res = await api.get('/super-admin/announcements')
      return announcementListResponse.parse(res.data)
    },
  })

  const trimmedTitle = title.trim()
  const trimmedBody = body.trim()
  const titleError =
    touched && trimmedTitle.length < 3
      ? 'Title must be at least 3 characters.'
      : undefined
  const bodyError =
    touched && trimmedBody.length < 3
      ? 'Message must be at least 3 characters.'
      : undefined
  const canPublish = trimmedTitle.length >= 3 && trimmedBody.length >= 3

  const publishMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/super-admin/announcements', {
        title: trimmedTitle,
        body: trimmedBody,
        audience,
        priority,
      })
      return res.data
    },
    onSuccess: (result) => {
      const reach = Number(result?.recipientCount ?? 0)
      toast.success(
        reach > 0
          ? `Announcement published to ${formatNumber(reach)} ${
              reach === 1 ? 'person' : 'people'
            }.`
          : 'Announcement published. No active recipients matched the audience.',
      )
      setTitle('')
      setBody('')
      setAudience('all_users')
      setPriority('normal')
      setTouched(false)
      void qc.invalidateQueries({ queryKey: ['platform-announcements'] })
    },
    onError: (err) => {
      const { message, fields } = extractApiError(err)
      // Surface the first field error inline-ish via toast; the form is
      // simple enough that a toast is adequate feedback here.
      const firstField = fields ? Object.values(fields)[0] : null
      toast.error(firstField ?? message)
    },
  })

  const removeMutation = useMutation({
    mutationFn: async (id) => {
      await api.delete(`/super-admin/announcements/${id}`)
      return id
    },
    onSuccess: () => {
      toast.success('Announcement deleted successfully.')
      setConfirmDelete(null)
      void qc.invalidateQueries({ queryKey: ['platform-announcements'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  // Synchronous re-entry guard against duplicate publishes. The button's
  // `disabled={publishMutation.isPending}` only takes effect on the NEXT
  // render, so a fast double / triple-click can fire several `mutate()` calls
  // before React disables it — and the form isn't cleared until `onSuccess`,
  // so `canPublish` stays true — producing duplicate announcements at the same
  // instant (QA: rows 3–5 identical). This ref flips the moment the first click
  // lands and blocks the rest until the request settles.
  const publishingRef = useRef(false)
  function onPublish(e) {
    e.preventDefault()
    setTouched(true)
    if (!canPublish || publishingRef.current) return
    publishingRef.current = true
    publishMutation.mutate(undefined, {
      onSettled: () => {
        publishingRef.current = false
      },
    })
  }

  const rows = listQuery.data?.data ?? []

  // Bulk Delete — fan out to the SAME per-announcement endpoint the row uses.
  const bulkActions = [
    {
      label: 'Delete',
      icon: Trash2,
      danger: true,
      onClick: (ids, ctx) =>
        setBulkDel({ ids, clearSelection: ctx?.clearSelection }),
    },
  ]
  async function runBulkDelete() {
    if (!bulkDel) return
    setBulkRunning(true)
    let ok = 0
    const fails = []
    for (const id of bulkDel.ids) {
      try {
        await api.delete(`/super-admin/announcements/${id}`)
        ok += 1
      } catch (e) {
        fails.push(extractApiError(e).message)
      }
    }
    setBulkRunning(false)
    if (ok)
      toast.success(
        `${ok} ${ok === 1 ? 'announcement' : 'announcements'} deleted.`,
      )
    if (fails.length)
      toast.error(
        `${fails.length} failed: ${fails[0]}` +
          (fails.length > 1 ? ` (+${fails.length - 1} more)` : ''),
      )
    await qc.invalidateQueries({ queryKey: ['platform-announcements'] })
    bulkDel.clearSelection?.()
    setBulkDel(null)
  }

  const columns = useMemo(
    () => [
      {
        key: 'title',
        header: 'Title',
        pinnable: true,
        minWidth: 200,
        sortAccessor: (r) => (r.title ?? '').toLowerCase(),
        searchAccessor: (r) => r.title ?? '',
        exportValue: (r) => r.title ?? '',
        render: (r) => (
          <p
            className="font-medium text-[color:var(--color-ink-strong)] line-clamp-2"
            title={r.title}
          >
            {r.title}
          </p>
        ),
      },
      {
        key: 'body',
        header: 'Message',
        grow: true,
        minWidth: 320,
        sortAccessor: (r) => (r.body ?? '').toLowerCase(),
        searchAccessor: (r) => r.body ?? '',
        exportValue: (r) => r.body ?? '',
        render: (r) => (
          <p className="text-[color:var(--color-ink)] line-clamp-2" title={r.body}>
            {r.body}
          </p>
        ),
      },
      {
        key: 'audience',
        header: 'Audience',
        minWidth: 140,
        sortAccessor: (r) => r.audience ?? '',
        exportValue: (r) => AUDIENCE_LABEL[r.audience] ?? r.audience ?? '',
        render: (r) => (
          <span className="inline-flex items-start gap-1 text-[color:var(--color-ink)]">
            {r.audience === 'company_admins' ? (
              <Building2 size={13} className="mt-0.5 shrink-0" />
            ) : (
              <Users size={13} className="mt-0.5 shrink-0" />
            )}
            <span>{AUDIENCE_LABEL[r.audience] ?? r.audience}</span>
          </span>
        ),
      },
      {
        key: 'priority',
        header: 'Priority',
        minWidth: 110,
        sortAccessor: (r) => (r.priority === 'high' ? 1 : 0),
        exportValue: (r) => (r.priority === 'high' ? 'Priority' : 'Normal'),
        render: (r) => (
          <span
            className={
              'inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ' +
              (r.priority === 'high'
                ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-white/[0.06] dark:text-amber-300 dark:border-white/10'
                : 'bg-[color:var(--color-card-hover)] text-[color:var(--color-ink-muted)] border-[color:var(--color-rule)]')
            }
          >
            {r.priority === 'high' ? 'Priority' : 'Normal'}
          </span>
        ),
      },
      {
        key: 'recipientCount',
        header: 'Recipients',
        minWidth: 110,
        sortAccessor: (r) => Number(r.recipientCount ?? 0),
        exportValue: (r) => Number(r.recipientCount ?? 0),
        cellClassName: 'tabular-nums',
        render: (r) =>
          formatNumber(Number(r.recipientCount ?? 0)),
      },
      {
        key: 'createdAt',
        header: 'Sent at',
        minWidth: 150,
        sortAccessor: (r) => (r.createdAt ? new Date(r.createdAt) : null),
        exportValue: (r) => fmtDate(r.createdAt),
        cellClassName:
          'text-[color:var(--color-ink-muted)] whitespace-nowrap',
        render: (r) => fmtDate(r.createdAt),
      },
      {
        key: 'actions',
        // Singular: this column renders exactly ONE row action. Restore the
        // plural if a second is ever added — the eleven columns that already
        // carry two or more use "Actions".
        header: 'Action',
        pinnedRight: true,
        align: 'center',
        minWidth: 72,
        sortable: false,
        exportable: false,
        hideable: false,
        render: (r) => (
          <RowActionsMenu
            label={`Actions for ${r.title}`}
            items={[
              {
                key: 'delete',
                label: 'Delete',
                icon: Trash2,
                danger: true,
                onClick: () => setConfirmDelete(r),
              },
            ]}
          />
        ),
      },
    ],
    [],
  )

  return (
    <div>
      <Breadcrumb current="Announcements" homePath="/super-admin/dashboard" />

      <div className="mb-6">
        <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] inline-flex items-center gap-2">
          <Megaphone size={26} className="text-[color:var(--color-accent-strong)]" />
          Announcements
        </h1>
        <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
          Broadcast a message to every company. Recipients see it in their
          notification bell.
        </p>
      </div>

      {/* Compose card */}
      <form
        onSubmit={onPublish}
        className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6 mb-8"
        noValidate
      >
        {/* Two columns on desktop: the composer (Title + Message) fills the
         *  wider left column while the delivery options + publish button sit
         *  beside it, so the card is far shorter than a single tall stack.
         *  Collapses to one column below lg. */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            <TextField
              label="Title"
              required
              placeholder="Enter a Title"
              maxLength={TITLE_MAX}
              showCount
              value={title}
              error={titleError}
              onChange={(e) => setTitle(e.target.value)}
            />

            <div>
              <FieldLabel label="Message" required htmlFor="announcement-body" />
              <textarea
                id="announcement-body"
                rows={4}
                maxLength={BODY_MAX}
                placeholder="Enter a Message"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className={
                  'w-full px-3 py-2 rounded-lg border bg-[color:var(--color-card)] text-[color:var(--color-ink)] text-sm outline-none transition-colors focus:border-[color:var(--color-accent)] placeholder:text-[color:var(--color-ink-subtle)] ' +
                  (bodyError
                    ? 'border-[color:var(--color-priority-urgent)]'
                    : 'border-[color:var(--color-rule)]')
                }
              />
              <div className="mt-1 flex justify-between text-xs">
                <span
                  role={bodyError ? 'alert' : undefined}
                  className={
                    bodyError
                      ? 'text-[color:var(--color-priority-urgent)]'
                      : 'text-[color:var(--color-ink-muted)]'
                  }
                >
                  {bodyError ?? ' '}
                </span>
                <span
                  className={
                    'text-[color:var(--color-ink-muted)] ' +
                    (trimmedBody.length >= BODY_MAX
                      ? 'text-[color:var(--color-priority-urgent)] font-semibold'
                      : '')
                  }
                >
                  {body.length}/{BODY_MAX}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <div>
              <FieldLabel label="Audience" htmlFor="announcement-audience" />
              <select
                id="announcement-audience"
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] text-sm outline-none focus:border-[color:var(--color-accent)] cursor-pointer"
              >
                <option value="all_users">All members (every company)</option>
                <option value="company_admins">Company Admins only</option>
              </select>
            </div>
            <div>
              <FieldLabel label="Priority" htmlFor="announcement-priority" />
              <select
                id="announcement-priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] text-sm outline-none focus:border-[color:var(--color-accent)] cursor-pointer"
              >
                <option value="normal">Normal</option>
                <option value="high">High — flags as Priority</option>
              </select>
            </div>
            <div className="mt-auto flex justify-end">
              <Button
                type="submit"
                loading={publishMutation.isPending}
                disabled={!canPublish || publishMutation.isPending}
              >
                <Send size={14} />
                Publish Announcement
              </Button>
            </div>
          </div>
        </div>
      </form>

      {/* Broadcast history on the shared DataTable — client-side (the list
       *  loads fully), so search / sort / pagination / export all run in the
       *  browser. Delete lives in the row kebab. */}
      <DataTable
        tableId="platform-announcements"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        loading={listQuery.isLoading}
        error={
          listQuery.isError ? extractApiError(listQuery.error).message : null
        }
        onRetry={() => listQuery.refetch()}
        title="Announcements"
        exportBaseName="announcements"
        exportTitle="Announcements"
        selectable
        bulkActions={bulkActions}
        searchPlaceholder="Search Title or Message"
        defaultSort={[{ key: 'createdAt', dir: 'desc' }]}
        emptyState={{
          icon: Megaphone,
          title: 'No announcements published yet',
        }}
      />

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => removeMutation.mutate(confirmDelete.id)}
        confirming={removeMutation.isPending}
        title="Delete Announcement?"
        confirmLabel="Delete"
        variant="danger"
        message={
          confirmDelete
            ? `"${confirmDelete.title}" will be permanently deleted from the announcement history. Notifications already delivered to users are not recalled.`
            : ''
        }
      />

      <ConfirmDialog
        open={bulkDel !== null}
        onCancel={() => {
          if (!bulkRunning) setBulkDel(null)
        }}
        onConfirm={runBulkDelete}
        confirming={bulkRunning}
        title={`Delete ${bulkDel?.ids.length ?? 0} ${(bulkDel?.ids.length ?? 0) === 1 ? 'announcement' : 'announcements'}?`}
        confirmLabel="Delete"
        variant="danger"
        message={`${bulkDel?.ids.length ?? 0} selected ${(bulkDel?.ids.length ?? 0) === 1 ? 'announcement' : 'announcements'} will be permanently deleted from the history. Notifications already delivered are not recalled.`}
      />
    </div>
  )
}

